import { candidateEducationService } from '../services/candidate-education.service';
import { useCandidateMutation, type CandidateMutationParams } from './useCandidateMutation';
import type {
  CreateEducationRequest,
  UpdateEducationRequest,
} from '@/features/educations/types/education.types';

export interface CandidateEducationMutations {
  createEducation: (request: CreateEducationRequest) => void;
  updateEducation: (id: string, request: UpdateEducationRequest) => void;
  deleteEducation: (id: string) => void;
}

export function useCandidateEducationMutations({
  queryKey,
  isAnonymous,
  shareId,
}: CandidateMutationParams): CandidateEducationMutations {
  const createEducation = useCandidateMutation(
    queryKey,
    (request: CreateEducationRequest) =>
      isAnonymous
        ? candidateEducationService.createByShareId(shareId!, request)
        : candidateEducationService.create(request),
    { success: 'Education added', error: 'Failed to add education' }
  );
  const update = useCandidateMutation(
    queryKey,
    ({ id, request }: { id: string; request: UpdateEducationRequest }) =>
      isAnonymous
        ? candidateEducationService.updateByShareId(shareId!, id, request)
        : candidateEducationService.update(id, request),
    { success: 'Education updated', error: 'Failed to update education' }
  );
  const deleteEducation = useCandidateMutation(
    queryKey,
    (id: string) =>
      isAnonymous
        ? candidateEducationService.deleteByShareId(shareId!, id)
        : candidateEducationService.delete(id),
    { success: 'Education deleted', error: 'Failed to delete education' }
  );

  return {
    createEducation,
    updateEducation: (id, request) => update({ id, request }),
    deleteEducation,
  };
}
